import type { JSX } from 'react';
import { Fragment } from 'react';

import { Eye, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  SCORE_TONE_CLASSES,
  SCORES_STATUS_LABELS,
  familyDisplayName,
  formatScore,
  formatTokens,
  formatUsd,
  overallScore,
  runPromptVersionLabel,
  scoreTone,
  type AgentTestRun,
} from '../../model/run';
import InfoHint from './InfoHint';
import RunStatusBadge from './RunStatusBadge';

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const itemsCountOf = (run: AgentTestRun): number => {
  const summary = run.scoresSummary;
  if (!summary) return 0;
  return Math.max(...Object.values(summary).map((entry) => entry?.n ?? 0), 0);
};

interface RunsTableProps {
  runs: AgentTestRun[];
  onViewResults: (run: AgentTestRun) => void;
  onReRun?: (run: AgentTestRun) => void;
}

const HEADERS: { label: string; hint?: string; className: string }[] = [
  { label: 'Run', className: 'flex-1' },
  { label: 'Estado', className: 'w-[170px]' },
  { label: 'Escenarios', hint: 'Cantidad de escenarios incluidos en la última corrida.', className: 'w-[90px]' },
  {
    label: 'Corridas',
    hint: 'Veces que se ha corrido este run (original + re-runs). La fila muestra la última corrida; en el detalle puedes navegar y comparar entre todas.',
    className: 'w-[80px]',
  },
  {
    label: 'Score juez',
    hint: 'Promedio de todas las rúbricas del juez (0 a 1). Verde estable, ámbar revisar, rojo inestable.',
    className: 'w-[110px]',
  },
  { label: 'Costo', hint: 'Costo total en USD y tokens consumidos.', className: 'w-[130px]' },
  { label: 'Prompt', hint: 'Versión del prompt con la que corrió el run.', className: 'w-[110px]' },
  { label: 'Fecha', className: 'w-[130px]' },
  { label: 'Acciones', className: 'w-[80px] justify-end' },
];

const RunsTable = ({ runs, onViewResults, onReRun }: RunsTableProps): JSX.Element => (
  <div className="flex flex-col rounded-xl border border-border-default bg-bg-surface px-4 py-1">
    <div className="flex items-center gap-3 py-3">
      {HEADERS.map((header) => (
        <span
          key={header.label}
          className={cn('flex shrink-0 items-center gap-1 text-xs font-medium text-text-muted', header.className)}
        >
          {header.label}
          {header.hint ? <InfoHint text={header.hint} /> : null}
        </span>
      ))}
    </div>
    <div className="h-px bg-border-subtle" />

    {runs.map((run, index) => {
      const score = overallScore(run.scoresSummary);
      const stdDevs = run.scoresSummary
        ? Object.values(run.scoresSummary)
            .map((entry) => entry?.stdDev ?? 0)
            .reduce((acc, value, _, arr) => acc + value / arr.length, 0)
        : 0;
      const tone = score !== null ? scoreTone(score, stdDevs) : null;
      const itemsCount = itemsCountOf(run);
      const name = familyDisplayName(run);

      return (
        <Fragment key={run.id}>
          <div className="flex items-center gap-3 py-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-medium text-text-default" title={name}>
                {name}
              </span>
              <span className="font-mono text-[0.7rem] text-text-muted/70">{run.langfuseRunId ?? '—'}</span>
            </div>
            <div className="w-[170px] shrink-0">
              <RunStatusBadge status={run.status} />
            </div>
            <span className="w-[90px] shrink-0 text-sm text-text-muted">{run.scenarioIds.length} esc</span>
            <span className="w-[80px] shrink-0 text-sm text-text-muted">{run.familyCount ?? 1}</span>
            <div className="flex w-[110px] shrink-0 flex-col gap-0.5">
              {score !== null && tone !== null ? (
                <>
                  <span className={cn('text-sm font-semibold', SCORE_TONE_CLASSES[tone].text)}>
                    {formatScore(score)}
                  </span>
                  <span className="text-[0.7rem] text-text-muted">{itemsCount} ítems</span>
                </>
              ) : (
                <span className="text-sm text-text-muted" title={`Scores ${SCORES_STATUS_LABELS[run.scoresStatus]}`}>
                  {SCORES_STATUS_LABELS[run.scoresStatus]}
                </span>
              )}
            </div>
            <div className="flex w-[130px] shrink-0 flex-col gap-0.5">
              <span className="text-sm font-medium text-text-default">
                {formatUsd(run.totalCostUsd)}
                {run.status === 'running' ? ' · parcial' : ''}
              </span>
              <span className="text-[0.7rem] text-text-muted">{formatTokens(run.totalUsage)}</span>
            </div>
            <span className="w-[110px] shrink-0 font-mono text-[0.7rem] text-text-muted">
              {runPromptVersionLabel(run)}
            </span>
            <span className="w-[130px] shrink-0 text-xs text-text-muted">{formatDate(run.createdAt)}</span>
            <div className="flex w-[80px] shrink-0 items-center justify-end gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => onViewResults(run)}
                aria-label={`Ver resultados de «${name}»`}
              >
                <Eye className="size-4" aria-hidden />
              </Button>
              {onReRun ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onReRun(run)}
                  aria-label={`Re-run de «${name}»`}
                >
                  <RotateCcw className="size-4" aria-hidden />
                </Button>
              ) : null}
            </div>
          </div>
          {index < runs.length - 1 ? <div className="h-px bg-border-subtle" /> : null}
        </Fragment>
      );
    })}
  </div>
);

export default RunsTable;
